import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type FamilyRole = 'owner' | 'co_manager' | 'contributor' | 'scanner' | 'viewer';

interface FamilyMembershipRow {
  group_id: string;
  role: FamilyRole;
  family_groups: { id: string; owner_id: string; name: string } | null;
}

export interface FamilyRoleState {
  role: FamilyRole | null;
  groupId: string | null;
  ownerId: string | null;
  groupName: string | null;
  isMember: boolean;
  isOwnerOrCoManager: boolean;
  canAddDirect: boolean; // owner | co_manager | contributor
  canScanOnly: boolean;  // scanner
  canViewOnly: boolean;  // viewer
  loading: boolean;
}

/**
 * Resolves the current user's family role. If the user is in multiple groups
 * we surface the first one (usual case is one). The role drives nav gating
 * and the Scanner / Viewer experiences.
 */
export function useFamilyRole(): FamilyRoleState {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['family-role', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('family_members')
        .select('group_id, role, family_groups!inner(id, owner_id, name)')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();
      if (error) {
        console.warn('[useFamilyRole] fetch error:', error.message);
        return null;
      }
      return (data as unknown as FamilyMembershipRow) ?? null;
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  const role = (data?.role ?? null) as FamilyRole | null;
  const isOwnerOrCoManager = role === 'owner' || role === 'co_manager';

  return {
    role,
    groupId: data?.group_id ?? null,
    ownerId: data?.family_groups?.owner_id ?? null,
    groupName: data?.family_groups?.name ?? null,
    isMember: !!data,
    isOwnerOrCoManager,
    canAddDirect: role === 'owner' || role === 'co_manager' || role === 'contributor',
    canScanOnly: role === 'scanner',
    canViewOnly: role === 'viewer',
    loading: isLoading,
  };
}
